import { Box, Button, Chip, DialogActions, DialogContent, DialogTitle, Dropdown, FormControl, Input, Menu, MenuButton, MenuItem, Modal, ModalDialog, Typography } from "@mui/joy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import React from "react";
import { Util } from "../Helpers/Util";
import PortalTableLayout from "../components/PortalTableLayout";
import WorkOrderDialog from "../Dialogs/WorkOrderDialog";
import ReassignPackingListDialog from "../Dialogs/ReassignPackingListDialog";
import BypassFiftyPercentPaymentDialog from "../Dialogs/BypassFiftyPercentPaymentDialog";
import WorkOrderHistoryDialog from "../Dialogs/WorkOrderHistoryDialog";
import LinkToTicketDialog from "../Dialogs/LinkToTicketDialog";
import SignatureCanvas from "react-signature-canvas";
import WrappedButton from "../WrappedButton";

export interface PackingList {
	BuildingName: string;
	InvoiceNumber: string;
	SerialNumber: string;
	Mechanic: string;
	InvoiceHeaderID: string;
	PackingListID: string;
	Notes: string;
	HasSignature: boolean;
	ApprovedDate: string;
	ApprovedDateRaw: Date | null;
	TypeOfWork: string;
	HasTicket: boolean;
	TotalItems: number;
	RemainingItems: number;
	AllItemsCompleted: boolean;
	FiftyPercentPaid: string;
	FiftyPercentBypassedBy: string;
	CompletionDate: string;
	CompletionDateRaw: Date | null;
	TicketNumbers: string;
}

type SortColumn = "BuildingName" | "InvoiceNumber" | "SerialNumber" | "Mechanic" | "ApprovedDate" | "FiftyPercentPaid";
type SortDirection = "asc" | "desc";

const fiftyPercentSortRank = (paid: string) => {
	switch (paid) {
		case "No":
			return 0;
		case "Bypassed":
			return 1;
		case "Yes":
			return 2;
		default:
			return 3;
	}
};

const compareEstimateNumbers = (a: string, b: string) => {
	const numA = Number(a);
	const numB = Number(b);
	if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
		return numA - numB;
	}
	return a.localeCompare(b);
};

const fiftyPercentPaidCell = (paid: string, bypassedBy: string) => (
	<div>
		<Chip
			size="sm"
			variant="soft"
			color={paid === "Yes" ? "success" : paid === "Bypassed" ? "warning" : "danger"}>
			{paid}
		</Chip>
		{paid === "Bypassed" && bypassedBy && (
			<Typography level="body-xs" sx={{ mt: 0.5 }}>
				{bypassedBy}
			</Typography>
		)}
	</div>
);

interface ApprovedPackingListsProps {
	showFinished?: boolean;
}

export default function ApprovedPackingLists(props: ApprovedPackingListsProps) {
	const showFinished = props.showFinished ?? false;

	return <AssignedWorkOrdersContent showFinished={showFinished} />;
}

function AssignedWorkOrdersContent(props: { showFinished: boolean }) {
	const [data, setData] = React.useState<Array<PackingList>>();
	const [pageNumber, setPageNumber] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(25);
	const [globalFilter, setGlobalFilter] = React.useState("");
	const [workOrderModal, setWorkOrderModal] = React.useState(false);
	const [currentPackingListId, setCurrentPackingListId] = React.useState<string>("");
	const [currentTypeOfWork, setCurrentTypeOfWork] = React.useState("");
	const [currentNotes, setCurrentNotes] = React.useState("");
	const [reassignModal, setReassignModal] = React.useState(false);
	const [historyModal, setHistoryModal] = React.useState(false);
	const [bypassModal, setBypassModal] = React.useState(false);
	const [currentMechanic, setCurrentMechanic] = React.useState("");
	const [currentInvoiceNumber, setCurrentInvoiceNumber] = React.useState("");
	const [sortColumn, setSortColumn] = React.useState<SortColumn | null>(null);
	const [sortDirection, setSortDirection] = React.useState<SortDirection>("asc");
	const [signModal, setSignModal] = React.useState(false);
	const [isSaving, setIsSaving] = React.useState(false);
	const [currentBuildingName, setCurrentBuildingName] = React.useState("");
	const [linkToTicketModal, setLinkToTicketModal] = React.useState(false);
	const [sendingSmsId, setSendingSmsId] = React.useState<string | null>(null);
	const [smsCooldownUntil, setSmsCooldownUntil] = React.useState<Record<string, number>>({});
	const [, setCooldownTick] = React.useState(0);

	const SMS_COOLDOWN_MS = 30_000;

	let sigRef: any = null;

	React.useEffect(() => {
		const id = window.setInterval(() => setCooldownTick((t) => t + 1), 1000);
		return () => clearInterval(id);
	}, []);

	React.useEffect(() => {
		setPageNumber(0);
	}, [props.showFinished]);

	React.useEffect(() => {
		getData();
	}, []);

	const getData = () => {
		fetch("PackingList/GetPackingLists", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ approved: true }),
		})
			.then((x) => x.json())
			.then((x) => {
				setData(
					x.map((y: any) => {
						return {
							BuildingName: y.building_name,
							InvoiceNumber: y.invoice_number,
							SerialNumber: y.serial_number,
							Mechanic: y.mechanic_name,
							InvoiceHeaderID: y.invoice_header_id,
							PackingListID: y.packing_list_id,
							Notes: y.notes ?? "",
							HasSignature: y.has_signature ?? false,
							ApprovedDate: y.approved_date ? new Date(y.approved_date).toLocaleDateString() : "",
							ApprovedDateRaw: y.approved_date ? new Date(y.approved_date) : null,
							TypeOfWork: y.proposal_line_item_type ?? "",
							HasTicket: y.has_ticket ?? false,
							TotalItems: y.total_items ?? 0,
							RemainingItems: y.remaining_items ?? 0,
							AllItemsCompleted: y.all_items_completed ?? false,
							FiftyPercentPaid: y.fifty_percent_paid ?? "No",
							FiftyPercentBypassedBy: y.fifty_percent_bypassed_by ?? "",
							CompletionDate: y.completion_date ? Util.Formatter(y.completion_date, "DT") : "",
							CompletionDateRaw: y.completion_date ? new Date(y.completion_date) : null,
							TicketNumbers: y.ticket_numbers ?? "",
						} as PackingList;
					})
				);
			});
	};

	const openWorkOrderDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentBuildingName(inv.BuildingName);
		setCurrentInvoiceNumber(inv.InvoiceNumber);
		setCurrentTypeOfWork(inv.TypeOfWork);
		setCurrentNotes(inv.Notes);
		setWorkOrderModal(true);
	};

	const openReassignDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentMechanic(inv.Mechanic);
		setReassignModal(true);
	};

	const openHistoryDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentInvoiceNumber(inv.InvoiceNumber);
		setHistoryModal(true);
	};

	const openBypassDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentInvoiceNumber(inv.InvoiceNumber);
		setBypassModal(true);
	};

	const openLinkToTicketDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentBuildingName(inv.BuildingName);
		setCurrentInvoiceNumber(inv.InvoiceNumber);
		setLinkToTicketModal(true);
	};

	const getSmsCooldownRemaining = (packingListId: string) =>
		Math.max(0, Math.ceil(((smsCooldownUntil[packingListId] ?? 0) - Date.now()) / 1000));

	const isSmsOnCooldown = (packingListId: string) => getSmsCooldownRemaining(packingListId) > 0;

	const sendMechanicText = (inv: PackingList) => {
		if (isSmsOnCooldown(inv.PackingListID) || sendingSmsId === inv.PackingListID)
			return;

		setSmsCooldownUntil((prev) => ({ ...prev, [inv.PackingListID]: Date.now() + SMS_COOLDOWN_MS }));
		setSendingSmsId(inv.PackingListID);
		fetch("PackingList/SendMechanicWorkOrderSms", {
			method: "POST",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ packingListId: inv.PackingListID }),
		})
			.then((x) => x.json())
			.then((result: { success?: boolean; message?: string; error?: string }) => {
				if (result.error) {
					setSmsCooldownUntil((prev) => {
						const next = { ...prev };
						delete next[inv.PackingListID];
						return next;
					});
				}
				alert(result.error ?? result.message ?? "Text message sent.");
			})
			.finally(() => setSendingSmsId(null));
	};

	const openSignedPdf = (inv: PackingList) => {
		fetch("PackingList/GetSignedPdf", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				packingListId: inv.PackingListID,
			}),
		})
			.then((x) => x.text())
			.then((x) => {
				const blob = x;
				var tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + blob;
			});
	};

	const openUnsignedPdf = (inv: PackingList) => {
		fetch("PackingList/GetUnsignedPdf", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				estimateNumber: inv.InvoiceNumber,
			}),
		})
			.then((x) => x.text())
			.then((x) => {
				var tab = window.open();
				if (tab) tab.location.href = document.location.origin + "/pdfs/" + x;
			});
	};

	const openSignDialog = (inv: PackingList) => {
		setCurrentPackingListId(inv.PackingListID);
		setCurrentInvoiceNumber(inv.InvoiceNumber);
		setCurrentBuildingName(inv.BuildingName);
		setSignModal(true);
	};

	const saveSignature = () => {
		if (sigRef.isEmpty()) {
			alert("A signature is required.");
			return;
		}
		setIsSaving(true);

		var dt = {
			base64Image: sigRef.toDataURL(),
			packingListId: currentPackingListId,
			estimateNumber: currentInvoiceNumber,
			buildingName: currentBuildingName,
		};

		fetch("PackingList/SignApprovedPackingList", {
			method: "Post",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(dt),
		}).then((x) => {
			setSignModal(false);
			setIsSaving(false);
			getData();
		});
	};

	const handleSort = (column: SortColumn) => {
		if (sortColumn === column) {
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			setSortColumn(column);
			setSortDirection("asc");
		}
	};

	const tabFilteredData = data?.filter((x) => x.AllItemsCompleted === props.showFinished);

	const filteredData = tabFilteredData?.filter(
		(x) =>
			Util.isNullOrEmpty(globalFilter) ||
			x.BuildingName.toLowerCase().includes(globalFilter.toLowerCase()) ||
			x.InvoiceNumber.toString().includes(globalFilter) ||
			x.Mechanic.toLowerCase().includes(globalFilter.toLowerCase())
	);

	const filteredCount = filteredData?.length ?? 0;

	const sortedData = filteredData?.sort((a, b) => {
		if (!sortColumn) return 0;
		let comparison = 0;
		if (sortColumn === "ApprovedDate") {
			const dateA = a.ApprovedDateRaw?.getTime() ?? 0;
			const dateB = b.ApprovedDateRaw?.getTime() ?? 0;
			comparison = dateA - dateB;
		} else if (sortColumn === "FiftyPercentPaid") {
			comparison = fiftyPercentSortRank(a.FiftyPercentPaid) - fiftyPercentSortRank(b.FiftyPercentPaid);
		} else if (sortColumn === "InvoiceNumber") {
			comparison = compareEstimateNumbers(a.InvoiceNumber, b.InvoiceNumber);
		} else {
			comparison = a[sortColumn].localeCompare(b[sortColumn]);
		}
		return sortDirection === "asc" ? comparison : -comparison;
	});

	const thisRows = sortedData?.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize);

	const sortableColumns: { key: SortColumn; label: string }[] = [
		{ key: "BuildingName", label: "Building Name" },
		{ key: "InvoiceNumber", label: "Estimate #" },
		{ key: "SerialNumber", label: "Serial #" },
		{ key: "Mechanic", label: "Mechanic" },
		{ key: "ApprovedDate", label: "Approved Date" },
		{ key: "FiftyPercentPaid", label: "50% Paid" },
	];
	const columnCount = sortableColumns.length + 2 + (props.showFinished ? 2 : 0);
	const ellipsisCellSx = { maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;

	return (
		<Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
			<WorkOrderDialog
				open={workOrderModal}
				packingListId={currentPackingListId}
				clientName={currentBuildingName}
				estimateNumber={currentInvoiceNumber}
				typeOfWork={currentTypeOfWork}
				initialNotes={currentNotes}
				onClose={() => setWorkOrderModal(false)}
				onSaved={() => {
					setWorkOrderModal(false);
					getData();
				}}
			/>
			<ReassignPackingListDialog
				open={reassignModal}
				packingListId={currentPackingListId}
				currentMechanic={currentMechanic}
				onClose={() => setReassignModal(false)}
				onSaved={() => {
					setReassignModal(false);
					getData();
				}}
			/>
			<WorkOrderHistoryDialog
				open={historyModal}
				packingListId={currentPackingListId}
				invoiceNumber={currentInvoiceNumber}
				onClose={() => setHistoryModal(false)}
			/>
			<BypassFiftyPercentPaymentDialog
				open={bypassModal}
				packingListId={currentPackingListId}
				invoiceNumber={currentInvoiceNumber}
				onClose={() => setBypassModal(false)}
				onSaved={() => {
					setBypassModal(false);
					getData();
				}}
			/>
			<LinkToTicketDialog
				open={linkToTicketModal}
				packingListId={currentPackingListId}
				buildingName={currentBuildingName}
				estimateNumber={currentInvoiceNumber}
				onClose={() => setLinkToTicketModal(false)}
				onSaved={() => {
					setLinkToTicketModal(false);
					getData();
				}}
			/>
			<Modal open={signModal}>
				<ModalDialog>
					<DialogTitle>Signature</DialogTitle>
					<DialogContent>
						<SignatureCanvas
							ref={(ref: any) => {
								sigRef = ref;
							}}
							penColor="black"
							canvasProps={{ width: "900", height: "400", style: { border: "1px solid black", borderRadius: 0 } }}
						></SignatureCanvas>
					</DialogContent>
					<DialogActions>
						<WrappedButton onClick={saveSignature} busy={isSaving}>
							Save Signature
						</WrappedButton>
						<Button
							onClick={() => {
								setSignModal(false);
							}}
							color="primary">
							Close
						</Button>
					</DialogActions>
				</ModalDialog>
			</Modal>
			<PortalTableLayout
				toolbar={
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							justifyContent: "space-between",
							gap: 1.5,
							backgroundColor: "background.surface",
							border: "1px solid",
							borderColor: "divider",
							p: 1.5,
						}}>
						<Typography level="title-md" sx={{ m: 0, color: "text.secondary" }}>
							{props.showFinished ? "Finished Work Orders" : "Pending Assigned Work Orders"}
						</Typography>
						<FormControl sx={{ minWidth: 180 }}>
							<Input
								name="Search"
								placeholder="Global Search"
								onChange={(event) => {
									setGlobalFilter(event.target.value);
									setPageNumber(0);
								}}
								value={globalFilter}
							/>
						</FormControl>
					</Box>
				}
				pagination={{
					count: filteredCount,
					page: pageNumber,
					rowsPerPage: pageSize,
					rowsPerPageOptions: [5, 10, 25, 50],
					onPageChange: (_event, newPage) => setPageNumber(newPage),
					onRowsPerPageChange: (event) => {
						setPageSize(parseInt(event.target.value, 10));
						setPageNumber(0);
					},
				}}>
				<Table stickyHeader size="small" sx={{ tableLayout: "fixed", width: "100%", minWidth: props.showFinished ? 1050 : 900 }}>
					<TableHead>
						<TableRow>
							{sortableColumns.map(({ key, label }) => (
								<TableCell key={key} sortDirection={sortColumn === key ? sortDirection : false}>
									<TableSortLabel
										active={sortColumn === key}
										direction={sortColumn === key ? sortDirection : "asc"}
										onClick={() => handleSort(key)}>
										{label}
									</TableSortLabel>
								</TableCell>
							))}
							<TableCell>Items</TableCell>
							{props.showFinished && <TableCell>Ticket #</TableCell>}
							{props.showFinished && <TableCell>Completed</TableCell>}
							<TableCell>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{thisRows?.map((x) => {
							const assignedCount = x.TotalItems - x.RemainingItems;
							return (
								<TableRow key={x.PackingListID} hover sx={{ cursor: "pointer" }}>
									<TableCell sx={ellipsisCellSx} title={x.BuildingName}>
										{x.BuildingName}
									</TableCell>
									<TableCell>{x.InvoiceNumber}</TableCell>
									<TableCell>{x.SerialNumber}</TableCell>
									<TableCell>{x.Mechanic}</TableCell>
									<TableCell>{x.ApprovedDate}</TableCell>
									<TableCell>{fiftyPercentPaidCell(x.FiftyPercentPaid, x.FiftyPercentBypassedBy)}</TableCell>
									<TableCell>{x.TotalItems > 0 ? `${assignedCount}/${x.TotalItems} assigned` : "\u2014"}</TableCell>
									{props.showFinished && (
										<TableCell sx={ellipsisCellSx} title={x.TicketNumbers || undefined}>
											{x.TicketNumbers || "\u2014"}
										</TableCell>
									)}
									{props.showFinished && <TableCell>{x.CompletionDate || "\u2014"}</TableCell>}
									<TableCell>
										<Dropdown>
											<MenuButton variant="plain" size="sm">
												<MoreVertIcon />
											</MenuButton>
											<Menu>
												{!x.HasSignature && <MenuItem onClick={() => openUnsignedPdf(x)}>Open PDF</MenuItem>}
												{!x.HasSignature && <MenuItem onClick={() => openSignDialog(x)}>Sign Packing List</MenuItem>}
												{x.HasSignature && <MenuItem onClick={() => openSignedPdf(x)}>Open Signed PDF</MenuItem>}
												<MenuItem onClick={() => openWorkOrderDialog(x)}>Work Order</MenuItem>
												{x.Mechanic &&
													(() => {
														const cooldownRemaining = getSmsCooldownRemaining(x.PackingListID);
														const onCooldown = cooldownRemaining > 0;
														const isSending = sendingSmsId === x.PackingListID;
														return (
															<MenuItem disabled={onCooldown || isSending} onClick={() => sendMechanicText(x)}>
																{isSending
																	? "Sending Text..."
																	: onCooldown
																		? `Send Text (${cooldownRemaining}s)`
																		: "Send Text to Mechanic"}
															</MenuItem>
														);
													})()}
												{!x.AllItemsCompleted && <MenuItem onClick={() => openLinkToTicketDialog(x)}>Link to Ticket</MenuItem>}
												<MenuItem onClick={() => openReassignDialog(x)}>Reassign Tech</MenuItem>
												{!props.showFinished && x.FiftyPercentPaid === "No" && (
													<MenuItem onClick={() => openBypassDialog(x)}>Bypass 50% Payment</MenuItem>
												)}
												<MenuItem onClick={() => openHistoryDialog(x)}>View History</MenuItem>
											</Menu>
										</Dropdown>
									</TableCell>
								</TableRow>
							);
						})}
						{!thisRows?.length && (
							<TableRow>
								<TableCell colSpan={columnCount} align="center" sx={{ py: 3 }}>
									No records found.
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</PortalTableLayout>
		</Box>
	);
}
